import React from 'react'
import { Grid, styled, Typography, useTheme } from '@mui/material'
import { TabButtonGroup } from 'src/Components/TabButton'
import { ButtonGroupAction } from '@e3dc-react/shell/Types/component-types'

interface Props {
  disabled?: boolean
}

const MainContainer = styled('div')(({ theme }) => ({
  padding: theme.spacing(3),
  display: 'flex',
  flexDirection: 'row',
  width: '100%',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderWidth: 1,
  borderStyle: 'solid',
  borderColor: theme.palette.grey.grey06,
  borderRadius: theme.shape.borderRadius,
  marginBottom: 12,
}))

const Description = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  justifyContent: 'center',
}))

enum Variants {
  Auto = 'auto',
  FullWidth = 'fullWidth',
}

type Variant = `${Variants}`

interface Props {
  title: string
  description?: string
  toggleActions: ButtonGroupAction[]
  activeActionIndex: number
  variant?: Variant
  disabled?: boolean
  depth?: number
  children?: React.ReactNode
}

/**
 * A settings box with a single toggle and child fields. The child fields can be hidden (e.g. based on the single toggle)
 */
const SingleToggleWithChildSettingsBox: React.FC<Props> = ({
  title,
  description,
  toggleActions,
  activeActionIndex,
  children,
  variant = 'auto',
  disabled = false,
  depth = 0,
  ...rest
}) => {
  const itemSize = variant === Variants.FullWidth ? 12 : 6
  const theme = useTheme()
  const backgroundColorEnabled = depth % 2 === 0 ? theme.palette.grey.grey07 : theme.palette.background.paper

  return (
    <MainContainer {...rest} sx={{ backgroundColor: disabled ? theme.palette.grey.grey06 : backgroundColorEnabled }}>
      <Grid width={'100%'}>
        <Grid container alignItems={'center'}>
          <Grid item xs={12} sm={8} md={6}>
            <Description>
              <Typography variant="body1">{title}</Typography>
              {description && typeof description === 'string' ? <Typography variant="body2">{description}</Typography> : description}
            </Description>
          </Grid>
          <Grid item xs={12} sm={4} md={6}>
            <TabButtonGroup active={activeActionIndex} options={toggleActions} disabled={disabled} />
          </Grid>
        </Grid>

        <Grid item xs={12} md={itemSize} lg={itemSize} pt={3}>
          {children}
        </Grid>
      </Grid>
    </MainContainer>
  )
}

export default SingleToggleWithChildSettingsBox
